import { Box, Button, Stack } from "@mui/joy";
import { theme } from "styles/theme";
import React, { useEffect, useState } from "react";
import CustomTypography from "components/common/CustomTypography";
import { useMutation } from "@tanstack/react-query";
import { useOnboardingPromotionPage } from "../../../../hooks/useOnboardingPromotionPage";
import useMixpanelProfileMkt from "../../../../hooks/useMixpanelProfileMkt";
import { webFunnelApi } from "../../../../api/webFunnelApi";
import { captureException } from "../../../../utils/sentry";
import {
  MktVersionTypes,
  versionToPathPrefix,
} from "../../../../components/mkt/mktVersionUtils";
import mixpanel from "mixpanel-browser";
import { OnboardingDemographicType } from "../../../../api/onboardingApi";

interface SelectOption {
  id: number;
  label: string;
}

export const selectOptionList: SelectOption[] = [
  { id: 1, label: "직업인 (회사원, 사업자, 프리랜서 등)" },
  { id: 2, label: "학생 (중·고·대학생, 대학원생 등)" },
  { id: 3, label: "무직 (주부, 은퇴자 등)" },
];

export default function Demographic_4_Promotion({
  version = "v4",
}: {
  version?: MktVersionTypes;
}) {
  const [, setIsAnimating] = useState(false); // 애니메이션 상태 추가
  const pageKey = "demographic_4";

  const { singleSelectionId, updateSingleSelection, handleNext } =
    useOnboardingPromotionPage({
      pageKey,
      nextPageRoute: `/${versionToPathPrefix(version)}-onboarding/curriculum/1`,
    });

  const setPromotionPeople = useMixpanelProfileMkt(version);

  // Session storage에서 데이터 복원
  useEffect(() => {
    const storedData = sessionStorage.getItem("demographic_data");
    if (storedData) {
      try {
        const demographicData: OnboardingDemographicType =
          JSON.parse(storedData);
        if (demographicData.jobId) {
          updateSingleSelection(demographicData.jobId);
        }
      } catch (error) {
        console.error("Failed to parse demographic data:", error);
      }
    }
  }, []);

  const { mutate: submitJob } = useMutation(
    ({ distinctId, job }: { distinctId: string; job: string }) =>
      webFunnelApi.submitJob(distinctId, job),
    {
      onError: (error, variables) => {
        captureException(error, {
          tags: { action: "web_funnel_submit_job" },
          extra: { pageKey, job: variables?.job },
        });
      },
    }
  );

  function handleSubmit(optionId: number) {
    const selectedOption = selectOptionList.find((e) => e.id === optionId);

    if (selectedOption && selectedOption.label) {
      // Set Mixpanel People property
      setPromotionPeople({ 직업: selectedOption.label });

      const distinctId =
        localStorage.getItem("WEB_FUNNEL_DISTINCT_ID") ||
        (mixpanel as any).promotion?.get_distinct_id();

      if (distinctId) {
        submitJob({ distinctId, job: selectedOption.label });
      } else {
        console.warn(
          `[${pageKey}] WEB_FUNNEL_DISTINCT_ID를 찾지 못해 job submit을 건너뜁니다.`
        );
      }
    }
  }

  // 버튼 선택 로직
  const handleOptionChange = (optionId: number) => {
    updateSingleSelection(optionId);

    // Session storage 업데이트
    const storedData = sessionStorage.getItem("demographic_data");
    const demographicData: OnboardingDemographicType = storedData
      ? JSON.parse(storedData)
      : {};
    demographicData.jobId = optionId;
    sessionStorage.setItem("demographic_data", JSON.stringify(demographicData));

    handleSubmit(optionId);

    setTimeout(() => {
      setIsAnimating(false);
      handleNext({ singleSelectionId: optionId });
    }, 300);
  };

  return (
    <Stack spacing={5} sx={{ height: "100%" }}>
      <Stack spacing={2}>
        <CustomTypography
          variant="Subtitle"
          fontWeight={500}
          sx={{ color: theme.vars.palette.Content.Strong }}
        >
          {"주변 환경이 어떠한지도 치료에 있어 중요한 역할을 하지요."}
        </CustomTypography>
        <CustomTypography
          variant="Subtitle"
          fontWeight={500}
          sx={{ color: theme.vars.palette.Content.Strong }}
        >
          {"현재 어떤 일을 하시나요?"}
        </CustomTypography>
      </Stack>

      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <Stack
          direction={"column"}
          sx={{
            width: "100%",
            whiteSpace: "nowrap",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {selectOptionList.map(({ id, label }, optionIndex) => (
            <Stack key={`select_option_${id}`} sx={{ flexGrow: 1, gap: 1 }}>
              <Button
                fullWidth
                variant={"SingleSlim"}
                color={singleSelectionId === id ? "primary" : "secondary"}
                size={"lg"}
                sx={{
                  transition: "background-color 0.3s ease", // ease 애니메이션 추가
                }}
                onClick={() => {
                  handleOptionChange(id);
                }}
              >
                {label}
              </Button>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
}
